import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnButton from "@shared/components/SnButton";
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

const SN_PRD_WRK_REP_025 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(), // 연동정보 그리드
    grid2: React.createRef<SnGridRef>(), // 상세정보 그리드
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };
  const searchRef = useRef<any>(null);
  const today = new Date();
  const todayform = `${today.getFullYear()}${(today.getMonth() + 1)
    .toString()
    .padStart(2, "0")}${today.getDate().toString().padStart(2, "0")}`;
  const { userInfo } = global(); // Login user 정보
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [gridFocus, setGridFocus] = useState(false);
  //#endregion

  //#region panel 사이즈 관련
  const cd_menu = Menuprops.cd_menu || "SN_PRD_WRK_REP_025";
  const defaultSize = { panel1width: "69.5%", panel2width: "29.5%" };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);
  //#endregion

  //#region Initialize
  useEffect(() => {
    const fetchform = async () => {
      setDataSources({ grid1: [], grid2: [] });
      try {
        const defaultForm = {
          cd_biz: userInfo.cd_biz,
          cd_emp: userInfo.cd_emp,
          nm_emp: userInfo.nm_emp,
        };
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항
        setFormData({ ...rtn.formData, ...defaultForm }); // 조회조건 초기값
        setColums({
          grid1: rtn.grid.grid1, // H그리드 항목 설정
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    getGridCurrent("grid1").selectAndFocusRow(0);
    setGridFocus(false);
  }, [gridFocus]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    // onInsert,
    // onDelete,
    // onSave,
  }));

  //#region OnSearch
  const onSearch = async () => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = getGridCurrent("grid2").getChange();

    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        try {
          getGridCurrent("grid1").stopEditing();
          getGridCurrent("grid2").stopEditing();

          // H 필수값 체크
          const requiredValuesCheckH =
            getGridCurrent("grid1").requiredValuesCheck();
          if (!requiredValuesCheckH) {
            return;
          }

          //L 필수값 체크
          const requiredValuesCheckL =
            getGridCurrent("grid2").requiredValuesCheck();
          if (!requiredValuesCheckL) {
            return;
          }
        } catch (e) {}
        // await onSave();
      }
    }

    try {
      const params = {
        id: "sn_prd_wrk_rep_025_01",
        params: searchRef.current.getFormdata(),
      };

      setDataSources({ grid1: [], grid2: [] });

      const data: any = await fnGetDataSet(params);

      if (data[0] && data[0].length > 0) {
        setDataSources({ grid1: data[0], grid2: data[1] });
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      setGridFocus(true);
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  //#region Filter
  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      const no_log = selectedRow.no_log;
      getGridCurrent("grid2").applyFilters({ no_log: no_log });
      getGridCurrent("grid2").selectAndFocusRow(0);
    }
  };

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex w-full h-full min-w-0 space-x-1.5">
        <SnResizable
          direction={"width"}
          seq={1}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel1width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{
              rowSelection: "single",
              title: "연동정보",
              isEditable: true,
            }}
            events={{
              onFocusedRowChanged: onFocusedRowChanged,
            }}
          />
        </SnResizable>
        <SnResizable
          direction={"width"}
          seq={2}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel2width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <SnGrid
            ref={gridRefs.current["grid2"]} // Grid Name
            columnConfig={grids.grid2.columns} // 컬럼 설정 전달
            rowData={grids.grid2.dataSource} // Datasource
            etcProps={{
              rowSelection: "single",
              title: "상세정보",
              isEditable: false,
            }}
          />
        </SnResizable>
      </div>
    </div>
  );
});

export default SN_PRD_WRK_REP_025;
